import { useId, useState, type CSSProperties, type ReactNode } from 'react'

export type Gap = 'none' | 's' | 'm' | 'l'

const GAPS: Record<Gap, number> = { none: 0, s: 8, m: 16, l: 24 }
const FLEX = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  between: 'space-between',
} as const

/** Children in a column (or a row, `across`), evenly spaced. */
export function Stack({
  direction = 'down',
  gap = 'm',
  align = 'stretch',
  justify = 'start',
  wrap = false,
  children,
}: {
  direction?: 'down' | 'across'
  gap?: Gap
  align?: 'start' | 'center' | 'end' | 'stretch'
  justify?: 'start' | 'center' | 'end' | 'between'
  wrap?: boolean
  children: ReactNode
}) {
  const style: CSSProperties = {
    flexDirection: direction === 'across' ? 'row' : 'column',
    gap: GAPS[gap],
    alignItems: FLEX[align],
    justifyContent: FLEX[justify],
    flexWrap: wrap ? 'wrap' : 'nowrap',
  }
  return (
    <div className="ui-stack" style={style}>
      {children}
    </div>
  )
}

/** Children in equal columns: `columns` of them, or as many of at least `min` px as fit. */
export function Grid({
  columns,
  min = 200,
  gap = 'm',
  children,
}: {
  columns?: number | null
  min?: number
  gap?: Gap
  children: ReactNode
}) {
  const template = columns
    ? `repeat(${columns}, minmax(0, 1fr))`
    : `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`
  return (
    <div className="ui-grid" style={{ gridTemplateColumns: template, gap: GAPS[gap] }}>
      {children}
    </div>
  )
}

/** A card with an optional title, description, and things at its top right. */
export function Frame({
  title,
  description,
  aside,
  children,
}: {
  title?: ReactNode
  description?: ReactNode
  aside?: ReactNode
  children: ReactNode
}) {
  const head = title || description || aside
  return (
    <div className="card ui-frame">
      {head && (
        <div className="ui-frame-head">
          <div className="ui-frame-heading">
            {title && <h3 className="ui-frame-title">{title}</h3>}
            {description && <p className="ui-frame-desc">{description}</p>}
          </div>
          {aside && <div className="ui-frame-aside">{aside}</div>}
        </div>
      )}
      <div className="ui-frame-body">{children}</div>
    </div>
  )
}

export interface Panel {
  label: ReactNode
  count?: number | null
  body: ReactNode
}

/** Panels behind a row of tabs, one open at a time (the first to start). Arrow keys move between tabs. */
export function PanelTabs({ tabs }: { tabs: readonly Panel[] }) {
  const [open, setOpen] = useState(0)
  const id = useId()
  const current = Math.min(open, tabs.length - 1)
  const move = (by: number) => {
    const next = (current + by + tabs.length) % tabs.length
    setOpen(next)
    document.getElementById(`${id}-tab-${next}`)?.focus()
  }
  return (
    <div className="ui-panel-tabs">
      <div
        className="tabs"
        role="tablist"
        onKeyDown={(e) => (e.key === 'ArrowRight' ? move(1) : e.key === 'ArrowLeft' ? move(-1) : undefined)}
      >
        {tabs.map((t, i) => (
          <button
            key={i}
            id={`${id}-tab-${i}`}
            type="button"
            role="tab"
            className="tab"
            aria-selected={i === current}
            aria-controls={`${id}-panel`}
            tabIndex={i === current ? 0 : -1}
            onClick={() => setOpen(i)}
          >
            {t.label}
            {t.count != null && <span className="tab-count">{t.count}</span>}
          </button>
        ))}
      </div>
      <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-tab-${current}`} className="ui-panel-tabs-body">
        {tabs[current]?.body}
      </div>
    </div>
  )
}
